import React from 'react';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Checkbox } from '@/components/ui/checkbox';
import { QuestionnaireData } from '@/types/category';
import { CategoryQuestions as CategoryQuestionsType } from '.';
import { useAuth } from '@/contexts/AuthContext';

interface CategoryQuestionsProps {
  categoryConfig: CategoryQuestionsType;
  formData: QuestionnaireData;
  setFormData: (data: QuestionnaireData) => void;
  sessionType?: 'individual' | 'group';
  isPractitionerMode?: boolean;
  isGiftMode?: boolean;
}

const CategoryQuestions: React.FC<CategoryQuestionsProps> = ({
  categoryConfig,
  formData,
  setFormData,
  sessionType = 'individual',
  isPractitionerMode = false,
  isGiftMode = false
}) => {
  const { user } = useAuth();

  // Get user's profile name from auth context
  const userProfileName = user?.user_metadata?.full_name || user?.email || '';

  // Determine which questions to use based on session type, practitioner mode, and gift mode
  const isGroupSessionType = sessionType === 'group';

  let questionsToUse;
  if (isGiftMode) {
    // Gift mode - use gift questions with fallback to regular questions
    questionsToUse = categoryConfig.giftQuestions || categoryConfig.questions;
  } else if (isPractitionerMode && isGroupSessionType) {
    // Practitioner creating GROUP session
    questionsToUse = categoryConfig.practitionerGroupQuestions ||
                     categoryConfig.groupQuestions ||
                     categoryConfig.practitionerQuestions ||
                     categoryConfig.questions;
  } else if (isPractitionerMode) {
    // Practitioner creating INDIVIDUAL session
    questionsToUse = categoryConfig.practitionerQuestions || categoryConfig.questions;
  } else if (isGroupSessionType && categoryConfig.groupQuestions) {
    // Regular user creating GROUP session
    questionsToUse = categoryConfig.groupQuestions;
  } else {
    // Regular user creating INDIVIDUAL session
    questionsToUse = categoryConfig.questions;
  }

  // Helper function to render any field type dynamically
  const renderField = (fieldName: string, config: any) => {
    // Skip if config is not a question object (could be plain string/number)
    if (!config || typeof config !== 'object' || !config.type) {
      return null;
    }

    // Handle conditional fields
    if (config.conditional) {
      const conditionField = config.conditional.field;
      const conditionValue = config.conditional.value;
      if (formData[conditionField as keyof QuestionnaireData] !== conditionValue) {
        return null;
      }
    }

    const value = (formData as any)[fieldName] || '';
    const updateValue = (newValue: any) => {
      setFormData({ ...formData, [fieldName]: newValue });
    };

    switch (config.type) {
      case 'radio':
        return (
          <div key={fieldName}>
            <Label className="text-base font-medium mb-3 block">
              {config.label}
            </Label>
            <RadioGroup value={value} onValueChange={updateValue}>
              {config.options?.map((option: string) => (
                <div key={option} className="flex items-center space-x-2">
                  <RadioGroupItem value={option} id={`${fieldName}-${option}`} />
                  <Label htmlFor={`${fieldName}-${option}`}>{option}</Label>
                </div>
              ))}
            </RadioGroup>
          </div>
        );

      case 'select':
        return (
          <div key={fieldName}>
            <Label className="text-base font-medium mb-3 block">
              {config.label}
            </Label>
            <Select value={value} onValueChange={updateValue}>
              <SelectTrigger>
                <SelectValue placeholder={config.placeholder || `Choose ${config.label.toLowerCase()}`} />
              </SelectTrigger>
              <SelectContent>
                {config.options?.map((option: string) => (
                  <SelectItem key={option} value={option}>{option}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case 'textarea':
        const maxLength = config.maxLength || (fieldName === 'currentNews' ? 2000 : fieldName.includes('deeper') || fieldName.includes('Reason') ? 500 : 250);
        return (
          <div key={fieldName}>
            <Label className="text-base font-medium mb-3 block">
              {config.label}
            </Label>
            <Textarea
              value={value}
              onChange={(e) => {
                const val = e.target.value;
                if (val.length <= maxLength) {
                  updateValue(val);
                }
              }}
              placeholder={config.placeholder}
              className="min-h-[100px]"
              maxLength={maxLength}
            />
            <div className="text-xs text-muted-foreground mt-1">
              {value.length}/{maxLength} characters
            </div>
          </div>
        );

      case 'text':
        return (
          <div key={fieldName}>
            <Label className="text-base font-medium mb-3 block">
              {config.label}
            </Label>
            <Input
              value={value}
              onChange={(e) => updateValue(e.target.value)}
              placeholder={config.placeholder}
              disabled={fieldName === 'name' && !!user}
              className={fieldName === 'name' && !!user ? "bg-gray-50" : ""}
            />
            {fieldName === 'name' && user && (
              <div className="text-xs text-muted-foreground mt-1">
                Using your profile name for personalization
              </div>
            )}
          </div>
        );

      case 'slider':
        const sliderValue = typeof value === 'number' ? value : (config.defaultValue || 5);
        return (
          <div key={fieldName} className="p-4 bg-muted/30 rounded-lg border border-border">
            <Label className="text-base font-medium mb-4 block">
              {config.label}
            </Label>
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                <span className="text-sm text-muted-foreground font-medium min-w-[2rem]">
                  {config.min || 1}
                </span>
                <Slider
                  value={[sliderValue]}
                  onValueChange={([val]) => updateValue(val)}
                  min={config.min || 1}
                  max={config.max || 10}
                  step={1}
                  className="flex-1"
                />
                <span className="text-sm text-muted-foreground font-medium min-w-[2rem] text-right">
                  {config.max || 10}
                </span>
              </div>
              <div className="text-center">
                <span className="text-2xl font-bold text-primary">
                  {sliderValue}
                </span>
                {fieldName === 'explicitLanguageLevel' && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {sliderValue <= 3 ? '🟢 Mild - Minimal explicit language' :
                     sliderValue <= 6 ? '🟡 Moderate - Some explicit language' :
                     '🔴 Maximum - Full explicit language'}
                  </p>
                )}
              </div>
            </div>
          </div>
        );

      case 'checkbox':
        const isChecked = value === true || value === 'true';
        return (
          <div key={fieldName} className="flex items-start space-x-3 p-3 rounded-lg border border-border">
            <Checkbox
              id={fieldName}
              checked={isChecked}
              onCheckedChange={(checked) => updateValue(checked)}
              className="mt-1"
            />
            <div className="space-y-1 leading-none flex-1">
              <Label
                htmlFor={fieldName}
                className="text-sm font-medium cursor-pointer"
              >
                {config.label}
              </Label>
            </div>
          </div>
        );

      default:
        console.warn(`[CategoryQuestions] Unknown field type: ${config.type} for field: ${fieldName}`);
        return null;
    }
  };

  // Debug logging to identify which questions are being rendered
  React.useEffect(() => {
    console.log('[CategoryQuestions] Rendering questions:', {
      sessionType,
      isPractitionerMode,
      isGiftMode,
      isGroupSessionType,
      questionKeys: Object.keys(questionsToUse || {}),
      questionsWithTypes: Object.entries(questionsToUse || {})
        .filter(([_, config]) => config && typeof config === 'object')
        .map(([fieldName, config]: [string, any]) => ({ fieldName, type: config.type }))
    });
  }, [questionsToUse, sessionType, isPractitionerMode, isGiftMode, isGroupSessionType]);

  return (
    <div className="space-y-6">
      {Object.entries(questionsToUse)
        .filter(([_, config]) => config && typeof config === 'object' && config.type)
        .map(([fieldName, config]) => renderField(fieldName, config))}
    </div>
  );
};

export default CategoryQuestions;
